<template>
  <div id="jobs-list" class="mt-4">
    <table class="table table-hover shadow-lg">
      <thead class="thead-dark">
      <tr>
        <th scope="col">Id</th>
        <th scope="col">Name</th>
        <th scope="col">Group name</th>
        <th scope="col">Last crawled</th>
        <th scope="col" style="width: 20vh"></th>
        <th scope="col" style="width: 20vh">
          <button class="btn text-success btn-link" v-on:click="jobCreate">
            <i class="fas fa-plus"></i>
          </button>
        </th>
      </tr>
      </thead>

      <tbody>
      <tr v-for="item in items" v-bind:key="item.id">
        <td>{{ item.id }}</td>

        <td>
          {{ item.name }}
        </td>

        <td>
          {{ item.projectname }}
        </td>

        <td>
          <span v-if="item.last_run_at" :class="lastRunDotClass(item.last_run_status)" class="last-run-dot"></span>
          <span v-if="item.last_run_at">{{ relativeTime(item.last_run_at) }}</span>
          <span v-else class="text-muted">Never</span>
          <div v-if="item.scheduling_enabled && item.next_run_at" class="text-muted next-run">
            Next: {{ formatDate(item.next_run_at) }}
          </div>
        </td>

        <td id="button-sector-start-stop">

          <button
              class="btn text-success btn-link"
              v-bind:value="item.id"
              v-on:click="jobStart(item.id)"
              :disabled="loadingJobs.includes(item.id)"
          >
            <i v-if="!loadingJobs.includes(item.id)" class="far fa-play-circle"></i>
            <i v-else class="fas fa-spinner fa-spin"></i>
          </button>

          <button
              class="btn text-danger btn-link"
              v-bind:value="item.id"
              v-on:click="jobStop(item.id)"
              :disabled="loadingJobs.includes(item.id)"
          >
            <i class="fas fa-stop"></i>
          </button>

        </td>


        <td id="button-sector-edit-delete">

          <button
              class="btn text-secondary btn-link"
              v-bind:value="item.id"
              v-on:click="jobEdit(item.id)"
          >
            <i class="far fa-edit"></i>
          </button>

          <button
              class="btn text-info btn-link"
              v-bind:value="item.id"
              v-on:click="jobHistory(item.id)"
              title="History"
          >
            <i class="far fa-clock"></i>
          </button>

          <button
              class="btn text-danger btn-link"
              v-bind:value="item.id"
              v-on:click="jobDelete(item.id)"
          >
            <i class="fas fa-trash-alt"></i>
          </button>

        </td>

      </tr>
      </tbody>

    </table>

    <!-- History Modal -->
    <div v-if="historyModal.show" class="modal-backdrop-custom" @click.self="closeHistory">
      <div class="modal-box shadow-lg">
        <div class="modal-box-header">
          <h5 class="mb-0">Job History</h5>
          <div>
            <button
              v-if="historyModal.activities.length > 0"
              class="btn-stats-toggle mr-2"
              :class="{ 'btn-stats-toggle--active': historyModal.showStats }"
              @click="toggleStats"
            >
              <i class="fas fa-chart-line"></i>{{ historyModal.showStats ? 'Hide stats' : 'View stats' }}
            </button>
            <button class="btn btn-sm btn-link text-dark" @click="closeHistory">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="modal-box-body">
          <div v-if="historyModal.loading" class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x"></i>
          </div>
          <div v-else-if="historyModal.activities.length === 0" class="text-center text-muted py-4">
            No activity recorded yet.
          </div>
          <template v-else>
            <div v-if="historyModal.showStats" class="job-stats-chart-wrap mb-3">
              <div v-if="historyModal.statsLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
              </div>
              <div v-else-if="historyModal.statsRuns.length === 0" class="text-center text-muted py-4">
                No finished runs to chart yet.
              </div>
              <div v-else class="job-stats-chart-canvas">
                <job-stats-chart :runs="historyModal.statsRuns" :styles="chartStyles"></job-stats-chart>
              </div>
            </div>
          <table class="table table-sm mb-0">
            <thead class="thead-dark">
              <tr>
                <th>Action</th>
                <th>Started at</th>
                <th>Finished at</th>
                <th>Duration</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="activity in historyModal.activities" :key="activity.id">
                <td>
                  <span :class="actionBadgeClass(activity.action)">
                    {{ actionLabel(activity.action) }}
                  </span>
                </td>
                <td>{{ formatDate(activity.started_at) }}</td>
                <td>{{ formatDate(activity.finished_at) }}</td>
                <td>{{ duration(activity.started_at, activity.finished_at) }}</td>
                <td>
                  <button
                    v-if="activity.has_log"
                    class="btn-log-icon"
                    :class="{ 'btn-log-icon--open': historyModal.openLogId === activity.id }"
                    @click="toggleLog(activity)"
                    title="View log"
                  >
                    <i :class="historyModal.openLogId === activity.id ? 'fas fa-chevron-up' : 'fas fa-file-alt'"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="historyModal.openLogId && historyModal.logContent !== null">
                <td colspan="5" class="p-0">
                  <div v-if="historyModal.logLoading" class="text-center py-2">
                    <i class="fas fa-spinner fa-spin"></i>
                  </div>
                  <div v-else>
                    <div class="log-toolbar">
                      <button
                        class="btn-log-toggle"
                        :class="{ 'btn-log-toggle--raw': historyModal.showRawLog }"
                        @click="historyModal.showRawLog = !historyModal.showRawLog"
                      >
                        <i :class="historyModal.showRawLog ? 'fas fa-list-ul' : 'fas fa-code'"></i>
                        {{ historyModal.showRawLog ? 'Show summary' : 'Show full log' }}
                      </button>
                    </div>

                    <!-- Raw log -->
                    <pre v-if="historyModal.showRawLog" class="log-viewer">{{ historyModal.logContent }}</pre>

                    <!-- Structured summary -->
                    <div v-else-if="parsedLog" class="log-parsed">

                      <!-- Stats bar -->
                      <div v-if="parsedLog.stats" class="log-stats-bar">
                        <div class="log-stat">
                          <div class="log-stat-value">{{ parsedLog.stats.PagesProcessed }}</div>
                          <div class="log-stat-label">Pages crawled</div>
                        </div>
                        <div class="log-stat">
                          <div class="log-stat-value text-info">{{ parsedLog.stats.ItemsOutputted }}</div>
                          <div class="log-stat-label">Items extracted</div>
                        </div>
                        <div class="log-stat">
                          <div class="log-stat-value">{{ parsedLog.stats.RequestsMade }}</div>
                          <div class="log-stat-label">Requests made</div>
                        </div>
                        <div class="log-stat">
                          <div class="log-stat-value">{{ parsedLog.stats.FromCache }}</div>
                          <div class="log-stat-label">From cache</div>
                        </div>
                      </div>

                      <!-- Listing pages -->
                      <div class="log-section">
                        <div class="log-section-title" @click="historyModal.showListingPages = !historyModal.showListingPages">
                          <i :class="historyModal.showListingPages ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="mr-1"></i>
                          <strong>Listing pages crawled</strong>
                          <span class="badge badge-secondary ml-2">{{ parsedLog.listingPages.length }}</span>
                        </div>
                        <div v-if="historyModal.showListingPages" class="log-url-list">
                          <div v-for="url in parsedLog.listingPages" :key="url" class="log-url-row">
                            <i class="fas fa-link mr-1 text-muted"></i>{{ url }}
                          </div>
                        </div>
                      </div>

                      <!-- Extracted items -->
                      <div class="log-section">
                        <div class="log-section-title">
                          <strong>Items extracted</strong>
                          <span class="badge badge-info ml-2">{{ parsedLog.detailPages.length }}</span>
                        </div>
                        <div class="log-items-list">
                          <div v-for="page in parsedLog.detailPages" :key="page.url" class="log-item-row">
                            <div class="log-item-url">{{ page.url }}</div>
                            <div class="log-item-fields">
                              <span v-for="(count, field) in page.fields" :key="field"
                                    :class="count > 0 ? 'log-field-badge log-field-badge--has' : 'log-field-badge log-field-badge--empty'">
                                {{ field }}<template v-if="count > 1"> ×{{ count }}</template>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>


<script>

import axios from "axios";
import statusCheck from "/src/utils/statusCheck.js";
import tokenRefresh from "/src/utils/tokenRefresh.js";
import Swal from 'sweetalert2'
import JobStatsChart from '/src/components/JobStatsChart.vue'


export default {

  name: 'jobs-list',

  components: {
    JobStatsChart,
  },

  data() {
    return {
      items: '',
      loadingJobs: [],
      historyModal: {
        show: false,
        loading: false,
        activities: [],
        currentJobId: null,
        openLogId: null,
        logContent: null,
        logLoading: false,
        showRawLog: false,
        showListingPages: false,
        showStats: false,
        statsLoading: false,
        statsRuns: [],
      },
    }
  },


  mounted() {

    if (!localStorage.getItem('reloaded')) {
      localStorage.setItem('reloaded', '1');
      location.reload();
    }


    axios.get(process.env.VUE_APP_CRAWLING_URL + '/api/jobs')
        .then((response) => {

          let data = [];

          for (let i = 0; i < response.data.length; i++) {

            data[i] = {
              name: response.data[i]["name"],
              id: response.data[i]["id"],
              projectname: response.data[i]["group_name"],
              last_run_at: response.data[i]["last_run_at"],
              last_run_status: response.data[i]["last_run_status"],
              scheduling_enabled: response.data[i]["scheduling_enabled"],
              next_run_at: response.data[i]["next_run_at"],
            };

          }

          this.items = data;

        })
        .catch((error) => {

          if (error.response) {
            statusCheck(error.response.status);
          }

        })

  },


  computed: {
    chartStyles() {
      return { position: 'relative', height: '260px', width: '100%' };
    },
    parsedLog() {
      if (!this.historyModal.logContent) return null;
      const lines = this.historyModal.logContent.split('\n').map(l => l.trim()).filter(Boolean);

      const result = { jobName: '', stats: null, listingPages: [], detailPages: [] };
      let blocks = [];
      let currentBlock = null;

      for (const line of lines) {
        if (line.startsWith('START ProcessJob:')) {
          const m = line.match(/Name=(.+)/);
          if (m) result.jobName = m[1].trim();
        } else if (line.startsWith('Job Statistics:')) {
          const stats = {};
          line.replace('Job Statistics:', '').split(';').forEach(p => {
            const [k, v] = p.trim().split('=');
            if (k && v !== undefined) stats[k.trim()] = parseInt(v.trim()) || 0;
          });
          result.stats = stats;
        } else if (line.startsWith('Download:')) {
          if (currentBlock) blocks.push(currentBlock);
          currentBlock = { url: line.replace('Download:', '').trim(), fields: null };
        } else if (currentBlock && line.startsWith('saveFields: page=') && line.includes('available fields:')) {
          const m = line.match(/available fields: ({.+})/);
          if (m) {
            try { currentBlock.fields = JSON.parse(m[1]); } catch (e) { /* ignore */ }
          }
        }
      }
      if (currentBlock) blocks.push(currentBlock);

      result.listingPages = blocks.filter(b => !b.fields).map(b => b.url);
      result.detailPages  = blocks.filter(b => b.fields);

      return result;
    },
  },

  methods: {

    jobHistory: function (id) {
      tokenRefresh();
      this.historyModal.show = true;
      this.historyModal.loading = true;
      this.historyModal.activities = [];
      this.historyModal.currentJobId = id;
      this.historyModal.openLogId = null;
      this.historyModal.logContent = null;
      this.historyModal.showStats = false;
      this.historyModal.statsRuns = [];
      this.historyModal.statsLoading = false;

      axios.get(process.env.VUE_APP_CRAWLING_URL + '/api/jobs/' + id + '/activities')
        .then((response) => {
          this.historyModal.activities = response.data;
        })
        .catch((error) => {
          if (error.response) {
            statusCheck(error.response.status);
          }
        })
        .finally(() => {
          this.historyModal.loading = false;
        });
    },

    closeHistory: function () {
      this.historyModal.show = false;
      this.historyModal.activities = [];
      this.historyModal.openLogId = null;
      this.historyModal.logContent = null;
      this.historyModal.showStats = false;
      this.historyModal.statsRuns = [];
      this.historyModal.statsLoading = false;
    },

    toggleStats: function () {
      this.historyModal.showStats = !this.historyModal.showStats;

      if (this.historyModal.showStats && this.historyModal.statsRuns.length === 0) {
        this.historyModal.statsLoading = true;
        const jobId = this.historyModal.currentJobId;
        axios.get(process.env.VUE_APP_CRAWLING_URL + '/api/jobs/' + jobId + '/activities/stats')
          .then((response) => {
            this.historyModal.statsRuns = response.data;
          })
          .catch((error) => {
            if (error.response) {
              statusCheck(error.response.status);
            }
          })
          .finally(() => {
            this.historyModal.statsLoading = false;
          });
      }
    },

    lastRunDotClass: function (status) {
      return status === 'finished' ? 'last-run-dot--success' : 'last-run-dot--running';
    },

    relativeTime: function (iso) {
      if (!iso) return '—';
      const seconds = Math.floor((Date.now() - new Date(iso)) / 1000);
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return minutes + 'm ago';
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return hours + 'h ago';
      const days = Math.floor(hours / 24);
      return days + 'd ago';
    },

    toggleLog: function (activity) {
      if (this.historyModal.openLogId === activity.id) {
        this.historyModal.openLogId = null;
        this.historyModal.logContent = null;
        return;
      }
      this.historyModal.openLogId = activity.id;
      this.historyModal.logContent = null;
      this.historyModal.logLoading = true;
      this.historyModal.showRawLog = false;
      this.historyModal.showListingPages = false;

      const jobId = this.historyModal.currentJobId;
      axios.get(process.env.VUE_APP_CRAWLING_URL + '/api/jobs/' + jobId + '/activities/' + activity.id + '/log')
        .then((response) => {
          this.historyModal.logContent = response.data.log || '(empty log)';
        })
        .catch((error) => {
          if (error.response) {
            statusCheck(error.response.status);
          }
          this.historyModal.logContent = '(failed to load log)';
        })
        .finally(() => {
          this.historyModal.logLoading = false;
        });
    },

    actionLabel: function (action) {
      const labels = {
        created: 'Created',
        updated: 'Updated',
        run:     'Run',
        stopped: 'Stopped',
      };
      return labels[action] || action;
    },

    actionBadgeClass: function (action) {
      const classes = {
        created: 'badge badge-success',
        updated: 'badge badge-primary',
        run:     'badge badge-info',
        stopped: 'badge badge-warning',
      };
      return classes[action] || 'badge badge-secondary';
    },

    formatDate: function (iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleString();
    },

    duration: function (start, end) {
      if (!start || !end) return '—';
      const ms = new Date(end) - new Date(start);
      if (ms < 1000) return ms + 'ms';
      const s = Math.floor(ms / 1000);
      if (s < 60) return s + 's';
      const m = Math.floor(s / 60);
      const rem = s % 60;
      return m + 'm ' + rem + 's';
    },

    jobEdit: function (id) {

      tokenRefresh();

      this.$router.push({
        name: "job-edit",
        params: {
          id: id
        }
      });

    },


    jobDelete: function (id) {

      tokenRefresh();


      Swal.fire({
        title: 'Info!',
        text: 'Are you sure',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Ok',
        cancelButtonText: 'Cancel',
        allowOutsideClick: true,

      })
      .then((response) => {

        if (response.isConfirmed) {

          axios.delete(
              process.env.VUE_APP_CRAWLING_URL + "/api/jobs/" + id
          )
          .then((response) => {

            if (response.status === 200) {

              Swal.fire({
                title: 'Success!',
                text: 'Job deleted',
                icon: 'success',
                confirmButtonText: 'Ok',
                allowOutsideClick: true,
                backdrop: true,
              })
              .then(() => {
                this.$router.go();
              });

            }

          })

        }

      })

    },


    jobStop: function (id) {

      tokenRefresh();


      axios.put(
          process.env.VUE_APP_CRAWLING_URL + "/api/jobs/" + id + "/stop"
      )
      .then((resp) => {


        if (resp.status === 200) {


          Swal.fire({
            title: 'Success!',
            text: 'Job stopped',
            icon: 'success',
            confirmButtonText: 'Ok',
            allowOutsideClick: true,
            backdrop: true,
          })
          .then(() => {

            this.$router.go();

          });


        }


      })

    },


    jobStart: function (id) {

      tokenRefresh();

      this.loadingJobs.push(id);

      Swal.fire({
        title: 'Job is running...',
        text: 'Please wait while the job completes.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      axios.get(
          process.env.VUE_APP_CRAWLING_URL + "/api/jobs/" + id + "/run"
      )
      .then((resp) => {

        if (resp.status === 200) {
          Swal.fire({
            title: 'Success!',
            text: 'Job finished',
            icon: 'success',
            confirmButtonText: 'Ok',
            allowOutsideClick: true,
            backdrop: true,
          })
          .then(() => {
            this.$router.go();
          });
        }

      })
      .catch((error) => {

        Swal.close();

        if (error.response) {
          statusCheck(error.response.status);
        }

      })
      .finally(() => {

        this.loadingJobs = this.loadingJobs.filter(
            jobId => jobId !== id
        );

      });

    },


    jobCreate: function () {

      tokenRefresh();

      this.$router.push({
        name: "job-create"
      });

    }


  }

}

</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 8px;
  width: 900px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.modal-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-box-body {
  overflow-y: auto;
  padding: 16px 20px;
}

.log-toolbar {
  background: #f8f9fa;
  padding: 5px 14px;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
}

.log-viewer {
  background: #f8f9fa;
  color: #333;
  font-size: 11px;
  line-height: 1.6;
  padding: 12px 16px;
  margin: 0;
  max-height: 420px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Structured parsed view */
.log-parsed {
  background: #fff;
  max-height: 480px;
  overflow-y: auto;
}

.log-stats-bar {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.log-stat {
  flex: 1;
  text-align: center;
  padding: 14px 8px;
  border-right: 1px solid #e9ecef;
}
.log-stat:last-child { border-right: none; }

.log-stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #212529;
  line-height: 1;
}

.log-stat-label {
  font-size: 10px;
  color: #6c757d;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.log-section {
  border-bottom: 1px solid #e9ecef;
}

.log-section-title {
  padding: 8px 14px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
}
.log-section-title:hover { background: #f8f9fa; }

.log-url-list {
  padding: 0 14px 10px 28px;
  max-height: 160px;
  overflow-y: auto;
}

.log-url-row {
  font-size: 11px;
  color: #0d6efd;
  padding: 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-items-list {
  padding: 0 14px 8px;
}

.log-item-row {
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
}
.log-item-row:last-child { border-bottom: none; }

.log-item-url {
  font-size: 12px;
  color: #0d6efd;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-item-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.log-field-badge {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 4px;
}
.log-field-badge--has {
  background: #d1e7dd;
  color: #0f5132;
}
.log-field-badge--empty {
  background: #f1f3f5;
  color: #adb5bd;
  text-decoration: line-through;
}

.last-run-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.last-run-dot--success {
  background: #198754;
}
.last-run-dot--running {
  background: #ffc107;
}

.next-run {
  font-size: 11px;
}

.job-stats-chart-wrap {
  padding: 10px 4px;
}

.job-stats-chart-canvas {
  position: relative;
  overflow: hidden;
}

.btn-stats-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #4e73df, #6f42c1);
  box-shadow: 0 2px 8px rgba(78, 115, 223, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.btn-stats-toggle:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(78, 115, 223, 0.5);
  color: #fff;
}
.btn-stats-toggle--active {
  background: linear-gradient(135deg, #17a673, #1cc88a);
  box-shadow: 0 2px 8px rgba(28, 200, 138, 0.4);
}
.btn-stats-toggle--active:hover {
  box-shadow: 0 4px 10px rgba(28, 200, 138, 0.5);
}

.btn-log-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #d0c8f0;
  border-radius: 14px;
  padding: 3px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #6f42c1;
  background: #f4f1fc;
  transition: background 0.15s ease;
}
.btn-log-toggle:hover {
  background: #e9e2fa;
  color: #6f42c1;
}
.btn-log-toggle--raw {
  color: #0d6efd;
  background: #eaf2ff;
  border-color: #c3dbff;
}
.btn-log-toggle--raw:hover {
  background: #dcebff;
  color: #0d6efd;
}

.btn-log-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #4e73df, #6f42c1);
  font-size: 11px;
  transition: transform 0.15s ease;
}
.btn-log-icon:hover {
  transform: scale(1.08);
  color: #fff;
}
.btn-log-icon--open {
  background: linear-gradient(135deg, #17a673, #1cc88a);
}
</style>
